import { _ } from "../../../core/polyglot/polyglot.js";
import AbstractReportExporter from "./AbstractReportExporter.js";
import ExcelExportingVisitor from "../visitors/exporters/Excel/ExcelExportingVisitor.js";
import XLSX from "xlsx-js-style";

/**
 * This exporter aims at downloading an Excel file out of a report.
 * For that, it uses SheetJS library to support feature like proper
 * xlsx export and multiple sheets per book.
 */
export default class ExcelExporter extends AbstractReportExporter {
	simpleLabel() {
		return _("Excel");
	}

	async bulkDownload({ reports, exportingSpec, exportingName }) {
		const worksheetData = await Promise.all(
			reports.map(async (report) => {
				const data = await this._makeReportData({
					report,
					exportingSpec,
				});

				return data[0];
			}),
		);

		this._downloadData({
			data: worksheetData,
			filename: this._nameWithExtension(exportingName),
		});
	}

	_exportingVisitorClass() {
		return ExcelExportingVisitor;
	}

	_filenameExtension() {
		return "xlsx";
	}

	async _makeReportData({ report, exportingSpec }) {
		const data = await super._makeReportData({
			report,
			exportingSpec,
		});

		return [
			this._convertReportDataToWorksheetDatum({
				data,
				name: report.getTitleString(),
			}),
		];
	}

	_downloadData({ data: worksheetData, filename }) {
		const workbook = this._makeWorkbook({ worksheetData });
		XLSX.writeFile(workbook, filename);
	}

	/**
	 * Convert the output of the Visitor into a SheetJS compatible worksheet.
	 *
	 * To do so, it has to do a couple of checks:
	 *   - compute each cell size to implement an "autofit" feature
	 *   - turn the CSS classes into Excel compatible properties
	 *   - fix some imported cell types
	 *
	 * @param {string} data - String representation of a DOMTableElement
	 * @param {string} name - Name of the worksheet to create
	 * @return {WorkSheetDatum}
	 */
	_convertReportDataToWorksheetDatum({
		data: { data, merges, rows, cols },
		name,
	}) {
		const worksheet = XLSX.utils.aoa_to_sheet(data);

		worksheet["!cols"] = cols;
		worksheet["!rows"] = rows;
		worksheet["!merges"] = merges;

		return { worksheet, name };
	}

	_makeWorkbook({ worksheetData = [] }) {
		const workbook = XLSX.utils.book_new();

		worksheetData.forEach(({ name, worksheet }) => {
			XLSX.utils.book_append_sheet(
				workbook,
				worksheet,
				// We need the slice because sheet names cannot exceed 31 chars
				name.slice(0, 31),
			);
		});

		return workbook;
	}
}

/**
 * @typedef WorkSheetDatum
 *
 * Object holding a worksheet and its name.
 * @type {object}
 * @property {string} name - Name of the worksheet
 * @property {WorkSheet} worksheet - The worksheet itself
 */
